import { z } from 'zod'

import {
  idSchema,
  isoInstantSchema,
  noteSchema,
  roleSchema,
  usernameSchema,
} from '../common'
import { passwordSchema } from '../auth/auth'

/**
 * O1–O2 · People (Owner Console designs 02 and 03).
 *
 * Roles are the money boundary, not a convenience: `STAFF` never receives a
 * money key from any endpoint (§4.16). Changing someone's role therefore
 * changes what the server *sends* them, which is why it is an owner action and
 * is audited.
 */
export const personSchema = z.object({
  id: idSchema,
  name: z.string(),
  username: usernameSchema,
  role: roleSchema,
  isActive: z.boolean(),
  mustChangePassword: z.boolean(),
  lastSignedInAt: isoInstantSchema.nullish(),
  note: noteSchema.nullish(),
  /** True for the caller's own row — the console refuses to demote or lock it. */
  isYou: z.boolean(),
})

export const peopleListResponse = z.array(personSchema)

export const createPersonRequest = z.object({
  name: z.string().trim().min(2).max(120),
  username: usernameSchema,
  role: roleSchema,
  /** Omit and the server generates one, returned once by `oneTimePassword`. */
  password: passwordSchema.optional(),
  note: noteSchema.optional(),
})

export const patchPersonRequest = z
  .object({
    name: z.string().trim().min(2).max(120).optional(),
    role: roleSchema.optional(),
    note: noteSchema.nullish(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

/**
 * Deactivate, never delete. The rows they entered stay theirs in the audit log,
 * and a deleted user makes a year of history read "unknown".
 */
export const deactivatePersonResponse = z.object({
  id: idSchema,
  isActive: z.literal(false),
  /** Their live sessions are destroyed in the same transaction. */
  sessionsRevoked: z.number().int().nonnegative(),
})

/**
 * O2 · `POST /people/:id/reset-password`.
 *
 * The one-time password is returned **once, in this response, and is never
 * retrievable again** — the owner reads it to the person over the phone. It is
 * stored only as a hash, and `mustChangePassword` is set so it survives exactly
 * one sign-in.
 */
export const resetPasswordResponse = z.object({
  userId: idSchema,
  oneTimePassword: z.string().min(8),
  expiresAt: isoInstantSchema,
  mustChangePassword: z.literal(true),
})

export type Person = z.infer<typeof personSchema>
export type CreatePersonRequest = z.input<typeof createPersonRequest>
export type PatchPersonRequest = z.input<typeof patchPersonRequest>
export type ResetPasswordResponse = z.infer<typeof resetPasswordResponse>
